import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InputField from "../InputField";
import {
    faPlus,
    faCheck,
    faPenToSquare,
    faTrashCan,
    faSearch
} from "@fortawesome/pro-regular-svg-icons";

import Switch from "../Switch";

export default function ManufacturerErrorCodesTable({ errorCodes, onChange, onDelete, onSave, savingRowId }) {
    const { t } = useTranslation();
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");

    const startEdit = (row) => setEditingId(row.errorPublicId);
    const stopEdit = () => setEditingId(null);

    const handleChange = (row, key, value) => {
        const updated = { ...row, [key]: value };
        onChange(updated);
    };

    const startAdd = () => {
        const newRow = {
            errorPublicId: "temp-" + Math.random().toString(36).substring(2, 9),
            isNew: true,
            errorCode: "",
            vendorErrorCode: "",
            info: "",
            description: "",
            customerAction: "",
            shouldNotify: false
        };

        onChange({ ...newRow, __forceAddToTop: true });

        setTimeout(() => setEditingId(newRow.errorPublicId), 0);
    };

    const filtered = errorCodes.filter((row) => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return (
            row.errorCode?.toLowerCase().includes(s) ||
            row.vendorErrorCode?.toLowerCase().includes(s) ||
            row.info?.toLowerCase().includes(s) ||
            row.description?.toLowerCase().includes(s) ||
            row.customerAction?.toLowerCase().includes(s)
        );
    });

    return (
        <>
            <div className="flex justify-end items-center mb-4 gap-4">
                <div className="w-64">
                    <InputField
                        icon={faSearch}
                        placeholder={t("manufacturer.searchErrorCodes")}
                        value={search}
                        onChange={(v) => setSearch(v.target.value)}
                    />
                </div>

                <button
                    className="btn btn-primary flex items-center gap-2 px-4 py-2 rounded-full"
                    onClick={startAdd}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("manufacturer.addErrorCode")}
                </button>
            </div>

            <table className="min-w-full border text-sm able-fixed">
                <thead className="bg-mute-100">
                    <tr>
                        <th className="border p-2">{t("logs.errorCode")}</th>
                        <th className="border p-2">{t("manufacturer.vendorCode")}</th>
                        <th className="border p-2">{t("common.info")}</th>
                        <th className="border p-2">{t("common.description")}</th>
                        <th className="border p-2">{t("manufacturer.customerAction")}</th>
                        <th className="border p-2">{t("manufacturer.shouldNotify")}</th>
                        <th className="border p-2 w-32"></th>
                    </tr>
                </thead>

                <tbody>
                    {filtered.map((row) => {
                        const isEditing = editingId === row.errorPublicId;

                        return (
                            <tr
                                key={row.errorPublicId}
                                className={`
        border 
        ${editingId === row.errorPublicId ? "border-2 border-blue-400 bg-blue-50" : ""}
    `}
                            >{isEditing ? (
                                <>
                                    <td className="border p-2">
                                        <textarea
                                            className="input w-full h-15"
                                            value={row.errorCode || ""}
                                            onChange={(e) => handleChange(row, "errorCode", e.target.value)}
                                        />
                                    </td>

                                    <td className="border p-2">
                                        <textarea
                                            className="input w-full h-15"
                                            value={row.vendorErrorCode || ""}
                                            onChange={(e) => handleChange(row, "vendorErrorCode", e.target.value)}
                                        />
                                    </td>

                                    <td className="border p-2">
                                        <textarea
                                            className="input w-full h-15"
                                            value={row.info || ""}
                                            onChange={(e) => handleChange(row, "info", e.target.value)}
                                        />
                                    </td>

                                    <td className="border p-2">
                                        <textarea
                                            className="input w-full h-15"
                                            value={row.description || ""}
                                            onChange={(e) => handleChange(row, "description", e.target.value)}
                                        />
                                    </td>

                                    <td className="border p-2">
                                        <textarea
                                            className="input w-full h-15"
                                            value={row.customerAction || ""}
                                            onChange={(e) => handleChange(row, "customerAction", e.target.value)}
                                        />
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex justify-center items-center">
                                                <Switch
                                                    checked={!!row.shouldNotify}
                                                    onChange={(checked) =>
                                                        handleChange(row, "shouldNotify", checked)
                                                    }
                                                />
                                            </div>
                                        </td>


                                    <td className="border p-2 align-middle">
                                        <div className="flex items-center justify-center gap-3">
                                            <button
                                                className="px-3 py-1 btn btn-secondary transition flex items-center gap-2"
                                                onClick={async () => {
                                                    await onSave(row);
                                                    stopEdit();
                                                }}
                                                disabled={savingRowId === row.errorPublicId}
                                            >
                                                <FontAwesomeIcon icon={faCheck} />
                                                {savingRowId === row.errorPublicId ? t("common.saving") : t("common.save")}
                                            </button>

                                        </div>
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td className="border p-2 min-w-[80px] max-w-[100px] break-words whitespace-normal">
                                        {row.errorCode}
                                    </td>

                                    <td className="border p-2 min-w-[90px] max-w-[120px] break-words whitespace-normal">
                                        {row.vendorErrorCode}
                                    </td>

                                    <td className="border p-2 min-w-[120px] max-w-[180px] break-words whitespace-normal">
                                        {row.info}
                                    </td>

                                    <td className="border p-2 min-w-[150px] max-w-[220px] break-words whitespace-normal">
                                        {row.description}
                                    </td>

                                    <td className="border p-2 min-w-[150px] max-w-[220px] break-words whitespace-normal">
                                        {row.customerAction}
                                    </td>
                                        <td className="border p-2 min-w-[150px] max-w-[220px] break-words whitespace-normal">
                                            <div className="flex justify-center items-center">
                                                {row.shouldNotify ? t("common.yes") : t("common.no")}
                                            </div>
                                        </td>
                                    <td className="border px-4 py-3 align-middle">
                                        <div className="flex items-center justify-center gap-3">
                                            <button
                                                className="text-primary-500 hover:text-primary-600 transition"
                                                onClick={() => startEdit(row)}
                                            >
                                                <FontAwesomeIcon icon={faPenToSquare} className="text-xl" />
                                            </button>
                                            <button
                                                className="text-red-500 hover:text-red-600 transition"
                                                onClick={() => onDelete(row)}
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} className="text-xl" />
                                            </button>
                                        </div>
                                    </td>
                                </>
                            )}</tr>

                        );
                    })}
                </tbody>
            </table>
        </>
    );
}
